import { type Building, BuildingType, dollars, isRunway } from './buildings';
import { TICKS_PER_GAME_DAY, TICKS_PER_GAME_HOUR } from './constants';

/*
 * The state incentive grant for regional airports. Once the first runway is open, the ministry
 * offers a one-off grant. Accepting pays it out at once, on one condition: within a few days the
 * airport must put up a large sign saying it benefited from the grant (as real publicly funded
 * projects must). No sign by the deadline, and the grant is clawed back.
 */

/** Saved: append only. */
export const GrantState = {
  None: 0,
  Offered: 1,
  /** Accepted and paid; the sign is due by the deadline. */
  SignDue: 2,
  Fulfilled: 3,
  Repaid: 4,
  Declined: 5,
} as const;
export type GrantStateId = (typeof GrantState)[keyof typeof GrantState];

export const GRANT_AMOUNT = dollars(250_000);
/** The offer stays open this long. */
export const GRANT_OFFER_TICKS = 48 * TICKS_PER_GAME_HOUR;
/** Days to put the sign up after accepting. */
export const GRANT_SIGN_DAYS = 3;

export interface GrantWorld {
  time: number;
  money: number;
  grantState: number;
  grantAmount: number;
  /** Offer expiry while offered; sign deadline once accepted (game ticks). */
  grantDeadline: number;
  readonly buildings: readonly Building[];
}

export type GrantEvent =
  | { readonly type: 'grantOffered'; readonly amount: number }
  | { readonly type: 'grantFulfilled'; readonly amount: number }
  | { readonly type: 'grantRepaid'; readonly amount: number };

/** Whether the grant sign may be built (the grant was accepted). */
export function signAllowed(grantState: number): boolean {
  return grantState === GrantState.SignDue || grantState === GrantState.Fulfilled;
}

/** Accepting pays out; declining closes the offer for good. Returns the money paid out. */
export function answerGrant(w: GrantWorld, accept: boolean): number {
  if (!accept) {
    w.grantState = GrantState.Declined;
    return 0;
  }
  w.grantState = GrantState.SignDue;
  w.grantDeadline = w.time + GRANT_SIGN_DAYS * TICKS_PER_GAME_DAY;
  return w.grantAmount;
}

/**
 * Hourly: offer the grant once a runway is open, let an unanswered offer lapse, and check the
 * sign condition. Returns the event to report, if any. A clawback is charged to `penalties`.
 */
export function updateGrant(w: GrantWorld, penalties: (amount: number) => void): GrantEvent | null {
  switch (w.grantState) {
    case GrantState.None:
      if (w.buildings.some((b) => isRunway(b.type) && b.level >= 1)) {
        w.grantState = GrantState.Offered;
        w.grantAmount = GRANT_AMOUNT;
        w.grantDeadline = w.time + GRANT_OFFER_TICKS;
        return { type: 'grantOffered', amount: w.grantAmount };
      }
      return null;
    case GrantState.Offered:
      if (w.time >= w.grantDeadline) w.grantState = GrantState.Declined;
      return null;
    case GrantState.SignDue:
      if (w.buildings.some((b) => b.type === BuildingType.GrantSign && b.level >= 1)) {
        w.grantState = GrantState.Fulfilled;
        return { type: 'grantFulfilled', amount: w.grantAmount };
      }
      if (w.time >= w.grantDeadline) {
        w.grantState = GrantState.Repaid;
        w.money -= w.grantAmount;
        penalties(w.grantAmount);
        return { type: 'grantRepaid', amount: w.grantAmount };
      }
      return null;
    default:
      return null;
  }
}
